import angular from 'angular'

/**
 * The basic scaffolding of the page. Generally the contents of a vd-section has a max-width of 1100px, center aligned.
 * However if you provide the `full="true"` attribute, the section content will fill the entire width of the page.
 *
 * @role region
 */
angular
  .module('vend.ui')
  .directive(
    'vdSection',
    function ($window, $document, $timeout, elementService) {
      return {
        transclude: true,
        link(scope, $element, attrs) {
          const ngElement = angular.element
          const classes = elementService.getClasses($element)
          const type = attrs.type
          const sticky = attrs.sticky

          classes.push('vd-section')

          if (type) {
            classes.push(`vd-section--${type}`)
          }

          /*
           * Initialises the sticky section.
           *
           * @method initStickySection
           */
          function initStickySection() {
            const documentEl = $document[0]
            const vdMainContentNode = ngElement(
              documentEl.querySelector('.vd-main-content')
            )
            const usingOneNav = Boolean(vdMainContentNode.length)
            const windowNode = ngElement($window)

            // The one nav flex layout means the window doesn't scroll. Listen to the correct node for scroll event and pad
            // the appropriate page content node when the stick section is position fixed.
            const scrollNode = usingOneNav ? vdMainContentNode : windowNode
            const pageContentNode = usingOneNav
              ? vdMainContentNode
              : ngElement(documentEl.querySelector('.vd-nav-rest-of-page'))
            // When using vend-nav we need to take the height of the fixed position top nav into account
            const topNavOffset = usingOneNav ? 0 : 50

            const sideNavNode = ngElement(
              documentEl.querySelector('.vd-side-nav-container, .vd-sidebar')
            )
            const sectionNode = $element
            const stickyOffset = sectionNode[0].offsetTop - topNavOffset

            let windowResizeBound = false

            /**
             * Get the amount to offset the left of the section when position fixed. Determined as the current width of the
             * side navigation if it is visible.
             *
             * @method getOffsetLeft
             */
            function getOffsetLeft() {
              if (sideNavNode.length) {
                const { left, width } = sideNavNode[0].getBoundingClientRect()
                return width + left
              }
              return 0
            }

            /**
             * Handle the window resizing.
             *
             * @method onWindowResize
             */
            function onWindowResize() {
              // Wait 200ms before calling the scroll handler otherwise sometimes the width can get stuck in the wrong
              // position as the window resizes to a point where the side nav is toggled
              $timeout(scrollHandler, 200)
            }

            /*
             * Handles fixing the section to the bottom of the topbar, and adding the correct amount of top padding to the
             * content body to cator for the fixed section.
             *
             * @method scrollHandler
             */
            function scrollHandler() {
              // Check that the sticky node is currently visible (display != none). Otherwise ignore sticky behaviour.
              if (
                !(
                  sectionNode[0].offsetWidth ||
                  sectionNode[0].offsetHeight ||
                  sectionNode[0].getClientRects().length
                )
              ) {
                return
              }

              if (scrollNode[0].scrollTop > stickyOffset) {
                // Because we don't know the height of the action bar section, as the height despends on it's content, we
                // will calculate the height and set that as the top padding for the rest-of-page class provided by the nav.
                pageContentNode.css(
                  'padding-top',
                  `${topNavOffset + sectionNode[0].offsetHeight}px`
                )

                sectionNode.addClass('vd-section--fixed')
                sectionNode.css('left', `${getOffsetLeft()}px`)

                if (!windowResizeBound) {
                  windowNode.on('resize', onWindowResize)
                  windowResizeBound = true
                }
              } else {
                resetSection()
              }
            }

            /**
             * Reset the section position and styles. Also cleans up event handlers etc.
             *
             * @method resetSection
             */
            function resetSection() {
              sectionNode.removeClass('vd-section--fixed')
              sectionNode.css('left', '')
              pageContentNode.css('padding-top', '')

              if (windowResizeBound) {
                windowNode.off('resize', onWindowResize)
                windowResizeBound = false
              }
            }

            scrollNode.on('scroll', scrollHandler)
            scrollHandler()

            // Unbinding the scroll event, so it doesn't fire on every other page.
            scope.$on('$destroy', () => {
              resetSection()
              scrollNode.off('scroll', scrollHandler)
            })
          }

          if (sticky === '') {
            // We want the whole page to have rendered before initialising the sticky section, so we can accurately
            // calculate the sticky offset.
            $timeout(initStickySection)
          }

          attrs.$set('role', 'region')
          attrs.$set('class', classes.join(' '))
        },
        template(tElement, tAttrs) {
          if (tAttrs.full) {
            return '<ng-transclude></ng-transclude>'
          } else if (typeof tAttrs.sticky !== 'undefined') {
            return '<div class="vd-section-container"><div class="vd-section-wrap" ng-transclude></div></div>'
          } else {
            return '<div class="vd-section-wrap" ng-transclude></div>'
          }
        },
      }
    }
  )
